import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth, useSignUp } from "@clerk/clerk-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { TriangleLogo } from "@/components/TriangleLogo";
import { LegalLinks } from "@/components/shared/LegalLinks";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { authErrorMessage, isAlreadyVerified } from "@/lib/authMessages";

type Step = "form" | "verify" | "legal" | "requirements";

type PendingSignUp = {
  status: string | null;
  createdSessionId: string | null;
  missingFields?: readonly string[];
  unverifiedFields?: readonly string[];
};

const EDITABLE_REQUIREMENTS = [
  { field: "first_name", key: "firstName", label: "Primeiro Nome", autoComplete: "given-name" },
  { field: "last_name", key: "lastName", label: "Sobrenome", autoComplete: "family-name" },
  { field: "username", key: "username", label: "Nome de usuário", autoComplete: "username" },
  { field: "password", key: "password", label: "Senha", autoComplete: "new-password" },
] as const;

const REQUIREMENT_LABELS: Record<string, string> = {
  legal_accepted: "aceite dos Termos de Uso e ciência da Política de Privacidade",
  first_name: "primeiro nome",
  last_name: "sobrenome",
  password: "senha",
  email_address: "confirmação do email",
  phone_number: "confirmação do telefone",
  username: "nome de usuário",
};

function getPendingRequirementLabels(resource: PendingSignUp) {
  const fields = [
    ...(resource.missingFields ?? []),
    ...(resource.unverifiedFields ?? []),
  ];

  const labels = [
    ...new Set(
      fields.map((field) => REQUIREMENT_LABELS[field] ?? "dados obrigatórios do cadastro"),
    ),
  ];

  return labels.length > 0 ? labels : ["dados obrigatórios do cadastro"];
}

export default function Register() {
  const { isSignedIn } = useAuth();
  const { isLoaded, signUp, setActive } = useSignUp();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [step, setStep] = useState<Step>("form");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [email, setEmail] = useState(() => searchParams.get("email") ?? "");
  const [password, setPassword] = useState("");
  const [username, setUsername] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [acceptedLegal, setAcceptedLegal] = useState(false);
  const [pendingRequirements, setPendingRequirements] = useState<string[]>([]);
  const [missingFields, setMissingFields] = useState<readonly string[]>([]);
  const pendingOAuthHandled = useRef(false);
  const operationInFlight = useRef(false);
  const pendingCodePreparation = useRef<string | null>(null);
  // A manual sign-up prepares its own email code. Without this guard, the
  // OAuth completion effect also sees `missing_requirements` and sends a
  // second prepare request, leaving Clerk and this screen out of sync.
  const manualEmailVerificationStarted = useRef(false);

  const beginOperation = useCallback(() => {
    if (operationInFlight.current) return false;
    operationInFlight.current = true;
    setError(null);
    setLoading(true);
    return true;
  }, []);

  const endOperation = useCallback(() => {
    operationInFlight.current = false;
    setLoading(false);
  }, []);

  const completeRegistration = useCallback(async (sessionId: string | null | undefined) => {
    if (!sessionId) {
      setError("Cadastro concluído. Entre com seu email para continuar.");
      return;
    }
    if (!setActive) {
      setError("Não foi possível iniciar sua sessão. Tente entrar com seu email.");
      return;
    }
    try {
      await setActive({ session: sessionId });
    } catch {
      setError("Cadastro concluído, mas não foi possível iniciar sua sessão. Tente entrar com seu email.");
      return;
    }
    navigate("/");
  }, [navigate, setActive]);

  const showPendingRequirements = useCallback((resource: PendingSignUp) => {
    setPendingRequirements(getPendingRequirementLabels(resource));
    setMissingFields(resource.missingFields ?? []);
    setError(null);

    if (resource.missingFields?.includes("legal_accepted")) {
      setAcceptedLegal(false);
      setStep("legal");
      return;
    }

    setStep("requirements");
  }, []);

  const continueRegistration = useCallback(async (resource: PendingSignUp) => {
    if (!signUp) return;
    if (resource.status === "complete" || resource.createdSessionId) {
      await completeRegistration(resource.createdSessionId);
    } else if (resource.status === "missing_requirements") {
      if (resource.missingFields?.length) {
        showPendingRequirements(resource);
      } else if (resource.unverifiedFields?.includes("email_address")) {
        // Only prepare a code when Clerk still requires email verification.
        // Updating names, password or legal acceptance must preserve a verified email.
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        setCode("");
        setError(null);
        setStep("verify");
      } else {
        showPendingRequirements(resource);
      }
    } else {
      setError("Não foi possível concluir seu cadastro. Tente novamente.");
    }
  }, [completeRegistration, showPendingRequirements, signUp]);

  useEffect(() => {
    if (isSignedIn) navigate("/");
  }, [isSignedIn, navigate]);

  // Resume OAuth or an interrupted sign-up when Clerk still needs more data.
  // Only email-code preparation is automatic. Legal acceptance is always explicit.
  useEffect(() => {
    if (!isLoaded || !signUp) return;
    if (manualEmailVerificationStarted.current) return;
    if (signUp.status !== "missing_requirements") return;
    if (pendingOAuthHandled.current) return;
    if (!beginOperation()) return;
    pendingOAuthHandled.current = true;

    setEmail(signUp.emailAddress ?? "");
    setFirstName(signUp.firstName ?? "");
    setLastName(signUp.lastName ?? "");
    setUsername(signUp.username ?? "");
    void continueRegistration(signUp)
      .catch((err: unknown) => {
        setError(authErrorMessage(err, "sign-up-verification"));
      })
      .finally(endOperation);
  }, [beginOperation, continueRegistration, endOperation, isLoaded, signUp]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || operationInFlight.current) return;
    if (!acceptedLegal) {
      setError("Você precisa aceitar os Termos de Uso e declarar ciência da Política de Privacidade.");
      return;
    }
    if (!beginOperation()) return;
    const normalizedEmail = email.trim().toLowerCase();
    const registrationDraft = JSON.stringify([firstName, lastName, normalizedEmail, password, acceptedLegal]);
    let signUpCreated = pendingCodePreparation.current === registrationDraft;
    try {
      manualEmailVerificationStarted.current = true;
      if (!signUpCreated) {
        await signUp.create({
          firstName,
          lastName,
          emailAddress: normalizedEmail,
          password,
          legalAccepted: acceptedLegal,
        });
        signUpCreated = true;
        pendingCodePreparation.current = registrationDraft;
      }
      await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
      pendingCodePreparation.current = null;
      setError(null);
      setStep("verify");
    } catch (err: unknown) {
      if (!signUpCreated) {
        manualEmailVerificationStarted.current = false;
        pendingCodePreparation.current = null;
      }
      setError(
        signUpCreated
          ? "Não foi possível enviar o código de verificação. Tente novamente."
          : authErrorMessage(err, "sign-up"),
      );
    } finally {
      endOperation();
    }
  };

  const handleVerify = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || !beginOperation()) return;
    try {
      const result = await signUp.attemptEmailAddressVerification({ code });
      // Clerk can provide the session before the status update reaches this
      // resource. A created session is sufficient to complete registration.
      if (result.status === "complete" || result.createdSessionId) {
        await completeRegistration(result.createdSessionId);
      } else if (
        result.status === "missing_requirements" &&
        result.unverifiedFields?.includes("email_address")
      ) {
        setError("Seu email ainda precisa ser confirmado. Confira o código recebido e tente novamente.");
      } else if (result.status === "missing_requirements") {
        showPendingRequirements(result);
      } else {
        setError("Não foi possível concluir a verificação. Tente novamente.");
      }
    } catch (err: unknown) {
      // The previous attempt may have succeeded while its response was lost.
      // Reload before deciding; `signUp.status` here can be stale.
      if (isAlreadyVerified(err)) {
        try {
          const refreshed = await signUp.reload();
          if (refreshed.status === "complete" || refreshed.createdSessionId) {
            await completeRegistration(refreshed.createdSessionId);
            return;
          }
          if (
            refreshed.status === "missing_requirements" &&
            refreshed.unverifiedFields?.includes("email_address")
          ) {
            setError("Seu email ainda precisa ser confirmado. Confira o código recebido e tente novamente.");
            return;
          }
          if (refreshed.status === "missing_requirements") {
            showPendingRequirements(refreshed);
            return;
          }
        } catch {
          // Fall through to the localized error below.
        }
      }
      setError(authErrorMessage(err, "sign-up-verification"));
    } finally {
      endOperation();
    }
  };

  const handleLegalAcceptance = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || operationInFlight.current) return;
    if (!acceptedLegal) {
      setError("Você precisa aceitar os Termos de Uso e declarar ciência da Política de Privacidade.");
      return;
    }

    if (!beginOperation()) return;
    try {
      const result = await signUp.update({ legalAccepted: true });
      await continueRegistration(result);
    } catch (err: unknown) {
      setError(authErrorMessage(err, "sign-up"));
    } finally {
      endOperation();
    }
  };

  const requirementValues = { firstName, lastName, username, password };
  const requirementSetters = {
    firstName: setFirstName,
    lastName: setLastName,
    username: setUsername,
    password: setPassword,
  };
  const editableRequirements = EDITABLE_REQUIREMENTS.filter(({ field }) =>
    missingFields.includes(field),
  );

  const handleRequirementsSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!isLoaded || operationInFlight.current || !editableRequirements.length) return;

    const updates: Partial<typeof requirementValues> = {};
    for (const { key } of editableRequirements) {
      updates[key] = key === "password" ? requirementValues[key] : requirementValues[key].trim();
      if (!updates[key]) {
        setError("Preencha os dados pendentes para continuar.");
        return;
      }
    }

    if (!beginOperation()) return;
    try {
      const result = await signUp.update(updates);
      await continueRegistration(result);
    } catch (err: unknown) {
      setError(authErrorMessage(err, "sign-up"));
    } finally {
      endOperation();
    }
  };

  const title =
    step === "form"
      ? "Criar sua conta"
      : step === "verify"
        ? "Verificar email"
        : step === "legal"
          ? "Aceite necessário"
          : "Dados pendentes";

  const description =
    step === "form"
      ? "Preencha os dados para começar."
      : step === "verify"
        ? `Enviamos um código para ${email}.`
        : step === "legal"
          ? "Para concluir seu cadastro, confirme que leu os documentos abaixo."
          : "Ainda precisamos destes dados para concluir seu cadastro.";

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div
        className="pointer-events-none fixed inset-0 opacity-20"
        style={{
          background:
            "radial-gradient(ellipse 60% 40% at 50% 0%, hsl(var(--primary)) 0%, transparent 70%)",
        }}
      />

      <div className="relative z-10 w-full max-w-sm">
        <div className="flex flex-col items-center gap-3 mb-8">
          <TriangleLogo size="lg" animate />
          <div className="text-center">
            <h1 className="text-2xl font-bold text-foreground tracking-tight">
              Código do Poder
            </h1>
            <p className="text-sm text-muted-foreground mt-1">Instituto Rafael Medeiros</p>
          </div>
        </div>

        <div className="bg-card border border-border/60 rounded-2xl p-8 shadow-xl shadow-black/20">
          <h2 className="text-lg font-semibold text-foreground mb-1">{title}</h2>
          <p className="text-sm text-muted-foreground mb-6">{description}</p>

          {step === "form" && (
            <>
              <form onSubmit={(e) => void handleFormSubmit(e)} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1.5">
                    <Label htmlFor="firstName">Primeiro Nome</Label>
                    <Input
                      id="firstName"
                      type="text"
                      autoComplete="given-name"
                      placeholder="João"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      disabled={loading}
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? "register-form-error" : undefined}
                      className="h-11"
                    />
                  </div>
                  <div className="space-y-1.5">
                    <Label htmlFor="lastName">Segundo Nome</Label>
                    <Input
                      id="lastName"
                      type="text"
                      autoComplete="family-name"
                      placeholder="Silva"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      disabled={loading}
                      aria-invalid={Boolean(error)}
                      aria-describedby={error ? "register-form-error" : undefined}
                      className="h-11"
                    />
                  </div>
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="email">Email</Label>
                  <Input
                    id="email"
                    type="email"
                    autoComplete="email"
                    placeholder="seu@email.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    disabled={loading}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? "register-form-error" : undefined}
                    className="h-11"
                  />
                </div>

                <div className="space-y-1.5">
                  <Label htmlFor="password">Senha</Label>
                  <Input
                    id="password"
                    type="password"
                    autoComplete="new-password"
                    placeholder="••••••••"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    disabled={loading}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? "register-form-error" : undefined}
                    className="h-11"
                  />
                </div>

                <LegalAcceptanceCheckbox
                  checked={acceptedLegal}
                  onCheckedChange={setAcceptedLegal}
                  disabled={loading}
                  invalid={Boolean(error)}
                  describedBy={error ? "register-form-error" : undefined}
                />

                {error && <ErrorBanner id="register-form-error" message={error} />}

                <Button
                  type="submit"
                  className="w-full h-11 glow-primary-sm"
                  disabled={
                    !isLoaded || loading || !firstName || !email || !password || !acceptedLegal
                  }
                >
                  {loading ? "Criando conta..." : "Continuar"}
                </Button>
              </form>
            </>
          )}

          {step === "verify" && (
            <form onSubmit={(e) => void handleVerify(e)} className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="code">Código de verificação</Label>
                <Input
                  id="code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  placeholder="000000"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                  required
                  disabled={loading}
                  aria-invalid={Boolean(error)}
                  aria-describedby={error ? "register-code-error" : undefined}
                  className="h-11 tracking-widest text-center text-lg"
                  maxLength={6}
                />
              </div>

              {error && <ErrorBanner id="register-code-error" message={error} />}

              <Button
                type="submit"
                className="w-full h-11 glow-primary-sm"
                disabled={!isLoaded || loading || code.length < 6}
              >
                {loading ? "Verificando..." : "Confirmar"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => {
                  setStep("form");
                  setCode("");
                  setError(null);
                }}
                disabled={loading}
              >
                ← Voltar
              </Button>
            </form>
          )}

          {step === "legal" && (
            <form onSubmit={(e) => void handleLegalAcceptance(e)} className="space-y-4">
              <LegalAcceptanceCheckbox
                checked={acceptedLegal}
                onCheckedChange={setAcceptedLegal}
                disabled={loading}
                invalid={Boolean(error)}
                describedBy={error ? "register-legal-error" : undefined}
              />

              {error && <ErrorBanner id="register-legal-error" message={error} />}

              <Button
                type="submit"
                className="w-full h-11 glow-primary-sm"
                disabled={!isLoaded || loading || !acceptedLegal}
              >
                {loading ? "Salvando..." : "Aceitar e continuar"}
              </Button>

              <Button
                type="button"
                variant="ghost"
                className="w-full text-muted-foreground"
                onClick={() => {
                  setStep("form");
                  setError(null);
                }}
                disabled={loading}
              >
                ← Voltar
              </Button>
            </form>
          )}

          {step === "requirements" && (
            <form onSubmit={(e) => void handleRequirementsSubmit(e)} className="space-y-4">
              <div
                role="status"
                aria-live="polite"
                className="rounded-lg border border-border/60 bg-muted/30 px-3 py-3 text-sm text-muted-foreground"
              >
                <p>Faltam:</p>
                <ul className="mt-2 list-disc space-y-1 pl-5">
                  {pendingRequirements.map((requirement) => (
                    <li key={requirement}>{requirement}</li>
                  ))}
                </ul>
              </div>

              {editableRequirements.map(({ field, key, label, autoComplete }) => (
                <div key={field} className="space-y-1.5">
                  <Label htmlFor={`pending-${key}`}>{label}</Label>
                  <Input
                    id={`pending-${key}`}
                    type={key === "password" ? "password" : "text"}
                    autoComplete={autoComplete}
                    value={requirementValues[key]}
                    onChange={(e) => requirementSetters[key](e.target.value)}
                    required
                    disabled={loading}
                    aria-invalid={Boolean(error)}
                    aria-describedby={error ? "register-requirements-error" : undefined}
                    className="h-11"
                  />
                </div>
              ))}

              {error && <ErrorBanner id="register-requirements-error" message={error} />}

              {editableRequirements.length > 0 ? (
                <Button
                  type="submit"
                  className="w-full h-11 glow-primary-sm"
                  disabled={
                    !isLoaded || loading ||
                    editableRequirements.some(({ key }) => !requirementValues[key].trim())
                  }
                >
                  {loading ? "Salvando..." : "Salvar e continuar"}
                </Button>
              ) : (
                <p className="text-sm text-muted-foreground">
                  Esta etapa ainda não está disponível nesta tela. Entre em contato com o suporte
                  para concluir seu cadastro.
                </p>
              )}
            </form>
          )}
        </div>

        <p className="text-center text-xs text-muted-foreground mt-6">
          Já tem conta?{" "}
          <Link to="/login" className="text-primary hover:underline">
            Entrar
          </Link>
        </p>
        <LegalLinks className="mt-4" openInNewTab />
      </div>
    </main>
  );
}


function LegalAcceptanceCheckbox({
  checked,
  onCheckedChange,
  disabled,
  invalid,
  describedBy,
}: {
  checked: boolean;
  onCheckedChange: (checked: boolean) => void;
  disabled: boolean;
  invalid: boolean;
  describedBy?: string;
}) {
  return (
    <div className="flex items-start gap-2.5 pt-1">
      <Checkbox
        id="register-legal"
        checked={checked}
        onCheckedChange={(value) => onCheckedChange(value === true)}
        className="mt-0.5"
        disabled={disabled}
        aria-required="true"
        aria-invalid={invalid}
        aria-describedby={describedBy}
      />
      <Label
        htmlFor="register-legal"
        className="cursor-pointer text-xs leading-relaxed text-muted-foreground"
      >
        Li e aceito os{" "}
        <Link
          to="/legal/termos"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Termos de Uso
        </Link>
        {" "}e declaro ciência da{" "}
        <Link
          to="/legal/privacidade"
          target="_blank"
          rel="noopener noreferrer"
          className="text-primary hover:underline"
        >
          Política de Privacidade
        </Link>
        .
      </Label>
    </div>
  );
}

function ErrorBanner({ id, message }: { id: string; message: string }) {
  // A5-A11y — role="alert" + aria-live="assertive" so screen readers
  // interrupt-announce the error when registration fails (WCAG 4.1.3).
  return (
    <p
      id={id}
      role="alert"
      aria-live="assertive"
      aria-atomic="true"
      className="text-sm text-destructive bg-destructive/15 border border-destructive/30 rounded-lg px-3 py-2"
    >
      {message}
    </p>
  );
}
